import * as THREE from "three";
import { CSS2DRenderer } from "three/examples/jsm/renderers/CSS2DRenderer.js";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";

export class Base {
  constructor({ container, frustumHeight = 10 }) {
    this.container = container;
    this.frustumHeight = frustumHeight;

    this.scene = new THREE.Scene();
    this.camera = this.createCamera();
    this.renderer = this.createRenderer();

    this.raycaster = new THREE.Raycaster();
    this.raycaster.layers.set(1);

    this.CSS2DRenderer = this.createCSS2DRenderer();

    this.createLighting();
    this.registerBasicEvents();
    this.orbitControls = this.createOrbitControls();
  }

  cleanup = () => {
    console.log("Cleaning up Three.js scene...");

    // Dispose of controls
    if (this.orbitControls) {
      this.orbitControls.dispose();
      this.orbitControls = null;
    }

    // Dispose of scene objects
    if (this.scene) {
      this.scene.traverse((object) => {
        if (object.isMesh) {
          if (object.geometry) {
            object.geometry.dispose();
          }
          if (object.material) {
            if (Array.isArray(object.material)) {
              object.material.forEach((mat) => mat.dispose());
            } else {
              object.material.dispose();
            }
          }
        }
      });

      // Remove all children
      while (this.scene.children.length > 0) {
        this.scene.remove(this.scene.children[0]);
      }

      this.scene = null;
    }

    // Dispose renderer
    if (this.renderer) {
      this.renderer.dispose();
      this.renderer.forceContextLoss?.();
      if (this.renderer.domElement?.parentNode) {
        this.renderer.domElement.parentNode.removeChild(
          this.renderer.domElement,
        );
      }
      this.renderer.domElement = null;
      this.renderer = null;
    }

    // Dispose of label renderer
    if (this.CSS2DRenderer) {
      if (this.CSS2DRenderer.domElement?.parentNode) {
        this.CSS2DRenderer.domElement.parentNode.removeChild(
          this.CSS2DRenderer.domElement,
        );
      }
      this.CSS2DRenderer = null;
    }

    // Null out references
    this.camera = null;
    this.raycaster = null;

    // Event listeners
    window.removeEventListener("resize", this.handleResize);

    console.log("Cleanup complete.");
  };

  createOrbitControls() {
    const controls = new OrbitControls(this.camera, this.renderer.domElement);

    controls.enableDamping = true;
    controls.dampingFactor = 0.05;

    // Restrict vertical rotation: max and min polar angles (in radians)
    controls.maxPolarAngle = Math.PI / 2; // 90 degrees (no looking below ground level)
    controls.minPolarAngle = Math.PI / 4; // 45 degrees (restricts looking too high)

    // Restrict zoom levels (zoom in and out using orthographic size)
    controls.minZoom = 1; // Minimum zoom (restricts getting too close)
    controls.maxZoom = 10; // Maximum zoom (restricts getting too far)

    return controls;
  }

  createCSS2DRenderer() {
    const renderer = new CSS2DRenderer();
    renderer.setSize(this.container.clientWidth, this.container.clientHeight);
    renderer.domElement.style.position = "absolute";
    renderer.domElement.style.top = "0";
    renderer.domElement.style.pointerEvents = "none";
    this.container.appendChild(renderer.domElement);
    return renderer;
  }

  registerBasicEvents() {
    window.addEventListener("resize", this.handleResize);
  }

  handleResize = () => {
    this.resizeRendererToContainer(this.container);
  };

  resizeRendererToContainer = (containerElement) => {
    const width = containerElement.clientWidth;
    const height = containerElement.clientHeight;
    const aspect = width / height;
    const frustumWidth = this.frustumHeight * aspect;

    this.camera.left = -frustumWidth / 2;
    this.camera.right = frustumWidth / 2;
    this.camera.top = this.frustumHeight / 2;
    this.camera.bottom = -this.frustumHeight / 2;

    this.camera.updateProjectionMatrix();

    this.renderer.setSize(width, height);
    this.CSS2DRenderer.setSize(width, height);
  };

  createLighting() {
    const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
    directionalLight.position.set(5, 5, 5);
    this.scene.add(directionalLight);

    const ambientLight = new THREE.AmbientLight(0xffffff, 1);
    this.scene.add(ambientLight);
  }

  createCamera() {
    const { width, height } = this.calculateFrustumDimensionsForInitialRender();

    // args: left, right, top, bottom, near, far
    const camera = new THREE.OrthographicCamera(
      -width / 2,
      width / 2,
      height / 2,
      -height / 2,
      0.01,
      100,
    );
    camera.position.set(5, 5, 5);
    camera.lookAt(0, 0, 0);
    camera.layers.enable(1);
    return camera;
  }

  createRenderer() {
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(this.container.clientWidth, this.container.clientHeight);
    renderer.setPixelRatio(window.devicePixelRatio); // inc pixel ratio for high-DPI screens
    renderer.setClearColor(0xffffff);
    this.container.appendChild(renderer.domElement);
    return renderer;
  }

  calculateFrustumDimensionsForInitialRender() {
    const aspect = this.container.clientWidth / this.container.clientHeight;
    const frustumWidth = this.frustumHeight * aspect;
    return { width: frustumWidth, height: this.frustumHeight };
  }
}
